/*
 * Copyright (C) 2018 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */

import React, {Component} from 'react'
import {connect} from 'react-redux'
import {objectOf, arrayOf, func, oneOf, shape, string, number, bool} from 'prop-types'
import {Alert} from '@instructure/ui-alerts'
import {Flex} from '@instructure/ui-flex'
import {Text} from '@instructure/ui-text'
import {Heading} from '@instructure/ui-heading'

import {useScope as createI18nScope} from '@canvas/i18n'
import {windowConfirm} from '@canvas/util/globalUtils'

import * as AssignmentActions from '../assignment/AssignmentActions'
import GradersTable from './GradersTable/index'
import PostToStudentsButton from './PostToStudentsButton'
import ReleaseButton from './ReleaseButton'
import {
  SELECTED_GRADES_FROM_UNAVAILABLE_GRADERS,
  setReleaseGradesStatus,
} from '../assignment/AssignmentActions'

const I18n = createI18nScope('assignment_grade_summary')

// @ts-expect-error
function enumeratedStatuses(actions) {
  return [
    actions.SELECTED_GRADES_FROM_UNAVAILABLE_GRADERS,
    actions.FAILURE,
    actions.STARTED,
    actions.SUCCESS,
  ]
}

// @ts-expect-error
function validGradersSelected(gradesByStudentId) {
  return Object.values(gradesByStudentId).every(gradesByGraderId => {
    // @ts-expect-error
    const grades = Object.values(gradesByGraderId)
    // @ts-expect-error
    const selectedGrade = grades.find(grade => grade.selected)
    if (!selectedGrade) {
      return true
    }
    // @ts-expect-error
    const graderFound = ENV.GRADERS.find(grader => selectedGrade.graderId === grader.user_id)
    return !graderFound || graderFound.grader_selectable
  })
}

class Header extends Component {
  static propTypes = {
    assignment: shape({
      title: string.isRequired,
    }).isRequired,
    graders: arrayOf(
      shape({
        graderName: string,
        graderId: string.isRequired,
      }),
    ).isRequired,
    releaseGrades: func.isRequired,
    releaseGradesStatus: oneOf(enumeratedStatuses(AssignmentActions)),
    unmuteAssignment: func.isRequired,
    unmuteAssignmentStatus: oneOf(enumeratedStatuses(AssignmentActions)),
    provisionalGrades: objectOf(
      objectOf(
        shape({
          grade: string,
          graderId: string.isRequired,
          id: string.isRequired,
          score: number,
          selected: bool.isRequired,
          studentId: string.isRequired,
        }),
      ),
    ).isRequired,
    setReleaseGradesStatus: func.isRequired,
  }

  static defaultProps = {
    releaseGradesStatus: null,
    unmuteAssignmentStatus: null,
  }

  handleReleaseClick = () => {
    const message = I18n.t(
      'Are you sure you want to do this? It cannot be undone and will override existing grades in the gradebook.',
    )

    // This is stupid, but Chrome has an issue whereby confirm alerts sometimes just
    // cancel themselves in certain cases.
    // See https://stackoverflow.com/questions/51250430/chrome-dismisses-confirm-promps-immediately-without-any-user-interaction
    setTimeout(() => {
      // @ts-expect-error
      if (windowConfirm(message)) this.props.releaseGrades()
    }, 100)
  }

  handleUnmuteClick = () => {
    const message = I18n.t('Are you sure you want to post grades for this assignment to students?')

    // This is stupid, but Chrome has an issue whereby confirm alerts sometimes just
    // cancel themselves in certain cases.
    // See https://stackoverflow.com/questions/51250430/chrome-dismisses-confirm-promps-immediately-without-any-user-interaction
    setTimeout(() => {
      // @ts-expect-error
      if (windowConfirm(message)) this.props.unmuteAssignment()
    }, 100)
  }

  // @ts-expect-error
  UNSAFE_componentWillReceiveProps(nextProps) {
    // @ts-expect-error
    if (nextProps.provisionalGrades !== this.props.provisionalGrades) {
      this.updateStatus(nextProps)
    }
  }

  // @ts-expect-error
  updateStatus = nextProps => {
    const isValidSelection = validGradersSelected(nextProps.provisionalGrades)
    const status = !isValidSelection ? SELECTED_GRADES_FROM_UNAVAILABLE_GRADERS : null
    const shouldUpdateStatus = nextProps.releaseGradesStatus !== status
    if (shouldUpdateStatus) {
      // @ts-expect-error
      this.props.setReleaseGradesStatus(status)
    }
  }

  render() {
    return (
      <header>
        {/* @ts-expect-error */}
        {this.props.assignment.gradesPublished && (
          <Alert margin="0 0 medium 0" variant="info">
            <Text weight="bold">{I18n.t('Attention!')}</Text>{' '}
            {I18n.t('Grades cannot be modified from this page as they have already been released.')}
          </Alert>
        )}
        {/* @ts-expect-error */}
        {this.props.releaseGradesStatus ===
          AssignmentActions.SELECTED_GRADES_FROM_UNAVAILABLE_GRADERS && (
          <Alert margin="0 0 medium 0" variant="error">
            {I18n.t(
              'One or more grade selected was provided by a grader with inactive enrollments. Please update your selections to those provided by current graders.',
            )}
          </Alert>
        )}

        <Heading level="h1" margin="0 0 x-small 0">
          {I18n.t('Grade Summary')}
        </Heading>

        {/* @ts-expect-error */}
        <Text size="x-large">{this.props.assignment.title}</Text>

        <Flex as="div" margin="large 0 0 0">
          {/* @ts-expect-error */}
          {this.props.graders.length > 0 && (
            // @ts-expect-error
            <Flex.Item as="div" flex="1" shouldGrow={true}>
              <GradersTable />
            </Flex.Item>
          )}

          {/* @ts-expect-error */}
          <Flex.Item align="end" as="div" flex="2" shouldGrow={true}>
            <Flex as="div" justifyItems="end">
              <Flex.Item>
                <ReleaseButton
                  // @ts-expect-error
                  gradesReleased={this.props.assignment.gradesPublished}
                  margin="0 x-small 0 0"
                  onClick={this.handleReleaseClick}
                  // @ts-expect-error
                  releaseGradesStatus={this.props.releaseGradesStatus}
                />
              </Flex.Item>

              <Flex.Item>
                <PostToStudentsButton
                  // @ts-expect-error
                  assignment={this.props.assignment}
                  onClick={this.handleUnmuteClick}
                  // @ts-expect-error
                  unmuteAssignmentStatus={this.props.unmuteAssignmentStatus}
                />
              </Flex.Item>
            </Flex>
          </Flex.Item>
        </Flex>
      </header>
    )
  }
}

// @ts-expect-error
function mapStateToProps(state) {
  const {assignment, releaseGradesStatus, unmuteAssignmentStatus} = state.assignment

  return {
    assignment,
    graders: state.context.graders,
    releaseGradesStatus,
    unmuteAssignmentStatus,
    provisionalGrades: state.grades.provisionalGrades,
  }
}

// @ts-expect-error
function mapDispatchToProps(dispatch) {
  return {
    releaseGrades() {
      dispatch(AssignmentActions.releaseGrades())
    },
    unmuteAssignment() {
      dispatch(AssignmentActions.unmuteAssignment())
    },

    // @ts-expect-error
    setReleaseGradesStatus(status) {
      dispatch(setReleaseGradesStatus(status))
    },
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Header)
